import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PlaneTakeoff, PlaneLanding, Calendar, Users, ChevronRight, Check, Plus, Luggage, Briefcase, Mail, Share2, Download, Copy } from "lucide-react";
import "./transport.css";
import { BookingHeader, Pills, PrimaryBtn, Stepper, OptRow, PaymentMethods, PriceRow, SeatLegend, PlaneSeatMap, SecureNote, TicketCard } from "./components";
import { AIRPORTS, FLIGHTS, FLIGHT_DAYS, POPULAR_DESTS, FLIGHT_OPTIONS, PLANE_TAKEN, fmt, makeRef } from "./transportData";
import { saveTicket } from "./ticketStore";

const STEPS = ["search", "results", "details", "passengers", "options", "seat", "payment", "confirmation", "ticket"];
const TITLES = {
  search: "Réserver un billet d'avion", results: "Résultats des vols", details: "Détails du vol",
  passengers: "Informations passager", options: "Options et services", seat: "Choix du siège",
  payment: "Paiement", confirmation: "Confirmation", ticket: "Mon billet",
};

export default function FlightBookingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState("search");
  const [tripType, setTripType] = useState("Aller simple");
  const [from, setFrom] = useState("ABJ");
  const [to, setTo] = useState("BYK");
  const [day, setDay] = useState("sam");
  const [pax, setPax] = useState(1);
  const [flight, setFlight] = useState(null);
  const [passengers, setPassengers] = useState([{ civ: "M.", nom: "Dattié", prenom: "Cédric", dob: "1985-04-12", nat: "Ivoirienne", pieceType: "CNI", pieceNum: "CI0123456789" }]);
  const [activePax, setActivePax] = useState(0);
  const [opts, setOpts] = useState({ extraBag: false, seatOpt: false, insurance: false });
  const [seat, setSeat] = useState(null);
  const [payMethod, setPayMethod] = useState("orange");
  const [paying, setPaying] = useState(false);
  const [bookingRef, setBookingRef] = useState(null);

  const airport = (code) => AIRPORTS.find((a) => a.code === code);
  const dateLabel = `${FLIGHT_DAYS.find((d) => d.id === day).label} sept. 2026`;
  const total = flight
    ? flight.price * pax + (opts.extraBag ? FLIGHT_OPTIONS.extraBag : 0) + (seat ? FLIGHT_OPTIONS.seat : 0) + (opts.insurance ? FLIGHT_OPTIONS.insurance : 0)
    : 0;

  const goBack = () => {
    const i = STEPS.indexOf(step);
    if (step === "confirmation" || step === "ticket" || i === 0) return navigate("/search");
    if (step === "payment" && !opts.seatOpt) return setStep("options");
    setStep(STEPS[i - 1]);
  };
  const syncPax = (n) => {
    setPax(n);
    setPassengers((p) => {
      const next = [...p];
      while (next.length < n) next.push({ civ: "M.", nom: "", prenom: "", dob: "", nat: "Ivoirienne", pieceType: "CNI", pieceNum: "" });
      return next.slice(0, n);
    });
    setActivePax(0);
  };
  const setPaxField = (i, k, v) => setPassengers((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const paxValid = passengers.every((p) => p.nom.trim() && p.prenom.trim());

  const afterOptions = () => setStep(opts.seatOpt ? "seat" : "payment");
  const pay = async () => {
    if (paying) return;
    setPaying(true);
    const ref = makeRef("AYA", from, to);
    let anonId = localStorage.getItem("jsonfirst_civ_userid");
    if (!anonId) {
      anonId = (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `anon-${Date.now()}`;
      try { localStorage.setItem("jsonfirst_civ_userid", anonId); } catch { /* noop */ }
    }
    const token = localStorage.getItem("civ_user_token") || "";
    let fiscal = null;
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/v3/transport/bookings/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          client_ref: ref, kind: "avion", user_id: anonId,
          operator: { id: "op_aircotedivoire", name: flight.airline },
          origin: from, destination: to, travel_date: dateLabel,
          amount_fcfa: total, pay_channel: payMethod, passenger: `${passengers[0].prenom} ${passengers[0].nom}`,
          details: { vol: flight.num, seat: seat || "Libre", dep: flight.dep, arr: flight.arr },
        }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) {
        setPaying(false);
        toast.error("Paiement impossible — réessayez");
        return;
      }
      fiscal = b.booking?.fiscal || null;
    } catch {
      setPaying(false);
      toast.error("Erreur réseau — réessayez");
      return;
    }
    setPaying(false);
    setBookingRef(ref);
    saveTicket({
      type: "avion", ref, title: `${airport(from).city} (${from}) → ${airport(to).city} (${to})`,
      sub: `${flight.airline} ${flight.num} · ${dateLabel} · ${flight.dep}`, price: total,
      details: {
        Vol: flight.num, Date: dateLabel, Départ: `${flight.dep} · ${from}`, Arrivée: `${flight.arr} · ${to}`, Siège: seat || "Libre", Passager: `${passengers[0].prenom} ${passengers[0].nom}`,
        Paiement: "Payé ✓ (sandbox)",
        ...(fiscal && fiscal.number ? { "Reçu fiscal": `${fiscal.type} ${fiscal.number}` } : fiscal ? { "Reçu fiscal": "En vérification" } : {}),
      },
    });
    setStep("confirmation");
  };
  const shareTicket = async () => {
    const text = `Billet AYA ${bookingRef} — ${from} → ${to} ${flight.dep} (${flight.num})`;
    if (navigator.share) { try { await navigator.share({ text }); } catch { /* annulé */ } }
    else { await navigator.clipboard?.writeText(text); toast.success("Billet copié ✓"); }
  };

  return (
    <div className="tp-page" data-testid="flight-booking-page">
      <BookingHeader title={TITLES[step]} onBack={goBack} />
      <div className="tp-container">

        {step === "search" && (
          <>
            <Pills options={["Aller simple", "Aller-retour", "Multi-destinations"]} value={tripType} onChange={setTripType} testid="flight-triptype" />
            <label className="tp-input" data-testid="flight-from">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PlaneTakeoff size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}>
                  <label>Départ</label>
                  <select value={from} onChange={(e) => setFrom(e.target.value)} data-testid="flight-from-select">
                    {AIRPORTS.map((a) => <option key={a.code} value={a.code}>{a.city} ({a.code})</option>)}
                  </select>
                </span>
              </span>
            </label>
            <label className="tp-input" data-testid="flight-to">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <PlaneLanding size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}>
                  <label>Arrivée</label>
                  <select value={to} onChange={(e) => setTo(e.target.value)} data-testid="flight-to-select">
                    {AIRPORTS.filter((a) => a.code !== from).map((a) => <option key={a.code} value={a.code}>{a.city} ({a.code})</option>)}
                  </select>
                </span>
              </span>
            </label>
            <div className="tp-input" data-testid="flight-date">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Calendar size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}><label>Date de départ</label><span style={{ fontSize: 13.5, fontWeight: 700 }}>{dateLabel}</span></span>
              </span>
            </div>
            <div className="tp-input">
              <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Users size={15} color="#0E7A4F" />
                <span style={{ flex: 1 }}><label>Passagers</label></span>
                <Stepper value={pax} onChange={syncPax} unit="adulte" />
              </span>
            </div>
            <PrimaryBtn testid="flight-search-btn" onClick={() => setStep("results")}>Rechercher les vols</PrimaryBtn>

            <div className="tp-sechead"><div><h2>Destinations populaires</h2></div></div>
            <div className="tp-carousel" data-testid="flight-popular-dests">
              {POPULAR_DESTS.map((d) => (
                <button key={d.code} className="tp-destcard" data-testid={`flight-dest-${d.code}`}
                  onClick={() => { setTo(d.code); if (from === d.code) setFrom("ABJ"); toast.success(`Destination : ${d.name}`); }}>
                  <img src={d.img} alt={d.name} loading="lazy" />
                  <span className="b"><h3>{d.name}</h3><span className="p">à partir de</span><br /><span className="px">{fmt(d.price)}</span></span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "results" && (
          <>
            <div className="tp-summary" data-testid="flight-results-summary">
              <span className="t">
                <h3>{airport(from).city} ({from}) → {airport(to).city} ({to})</h3>
                <p>{dateLabel} · {pax} adulte{pax > 1 ? "s" : ""}</p>
              </span>
              <button className="tp-link" data-testid="flight-modify-btn" onClick={() => setStep("search")}>Modifier</button>
            </div>
            <div className="tp-days" data-testid="flight-days">
              {FLIGHT_DAYS.map((d) => (
                <button key={d.id} className={`tp-day ${day === d.id ? "on" : ""}`} data-testid={`flight-day-${d.id}`} onClick={() => setDay(d.id)}>
                  <span className="d">{d.label}</span><br /><span className="p">{(d.price / 1000).toFixed(0)} 000</span>
                </button>
              ))}
            </div>
            <div className="tp-chips">
              {["Tri : Heure départ", "Prix", "Compagnie"].map((f) => (
                <button key={f} className="tp-chip" onClick={() => toast.info("Filtres avancés bientôt disponibles.")}>{f}</button>
              ))}
            </div>
            {FLIGHTS.map((f) => (
              <button key={f.id} className="tp-result" data-testid={`flight-result-${f.id}`} onClick={() => { setFlight(f); setStep("details"); }}>
                <span className="toprow">
                  <span className="tp-airline"><span className="tp-logo" style={{ background: "#0E7A4F" }}>ACI</span>{f.airline}</span>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                    <span className="tp-price">{fmt(f.price)}</span><ChevronRight size={14} color="#A6B2A9" />
                  </span>
                </span>
                <span className="tp-times">
                  <span><span className="t">{f.dep}</span><br /><span className="a">{from}</span></span>
                  <span className="mid">{f.duration}</span>
                  <span style={{ textAlign: "right" }}><span className="t">{f.arr}</span><br /><span className="a">{to}</span></span>
                </span>
                <span className="tp-meta">
                  <span className="tp-tag">{f.cls}</span>
                  <span><Luggage size={10} style={{ verticalAlign: -1 }} /> Bagage cabine 7 kg</span>
                  <span><Briefcase size={10} style={{ verticalAlign: -1 }} /> Bagage soute 23 kg</span>
                </span>
              </button>
            ))}
          </>
        )}

        {step === "details" && flight && (
          <>
            <img src="/aya/transport/tp_vol_hero.jpg" alt="Vol Air Côte d'Ivoire" className="tp-hero" />
            <div className="tp-card">
              <div className="toprow" style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <span className="tp-airline"><span className="tp-logo" style={{ background: "#0E7A4F" }}>ACI</span>{flight.airline}</span>
                <span className="tp-tag">{flight.num}</span>
              </div>
              <div className="tp-times">
                <span><span className="t">{flight.dep}</span><br /><span className="a">{from} · {airport(from).city}</span></span>
                <span className="mid">{flight.duration} · Direct</span>
                <span style={{ textAlign: "right" }}><span className="t">{flight.arr}</span><br /><span className="a">{to} · {airport(to).city}</span></span>
              </div>
              <div className="tp-meta" style={{ marginTop: 10 }}>
                <span><Calendar size={10} style={{ verticalAlign: -1 }} /> {dateLabel}</span>
                <span><Luggage size={10} style={{ verticalAlign: -1 }} /> 7 kg cabine inclus</span>
                <span><Briefcase size={10} style={{ verticalAlign: -1 }} /> 23 kg soute inclus</span>
              </div>
              <PriceRow label="Classe" value="Économique" />
              <PriceRow total label={`${fmt(flight.price)} / passager`} value={pax > 1 ? `× ${pax} = ${fmt(flight.price * pax)}` : ""} />
            </div>
            <PrimaryBtn testid="flight-details-continue" onClick={() => setStep("passengers")}>Continuer</PrimaryBtn>
          </>
        )}

        {step === "passengers" && (
          <>
            <div className="tp-tabs" data-testid="flight-pax-tabs">
              {passengers.map((_, i) => (
                <button key={i} className={`tp-tab ${activePax === i ? "on" : ""}`} data-testid={`flight-pax-tab-${i}`} onClick={() => setActivePax(i)}>Adulte {i + 1}</button>
              ))}
              {pax < 4 && (
                <button className="tp-tab" data-testid="flight-add-pax" onClick={() => { syncPax(pax + 1); setActivePax(pax); }}>
                  <Plus size={11} style={{ verticalAlign: -1 }} /> Ajouter un passager
                </button>
              )}
            </div>
            {[passengers[activePax]].map((p) => (
              <div key={activePax}>
                <label className="tp-input"><label>Civilité</label>
                  <select value={p.civ} onChange={(e) => setPaxField(activePax, "civ", e.target.value)} data-testid="flight-pax-civ">
                    <option>M.</option><option>Mme</option><option>Mlle</option>
                  </select>
                </label>
                <label className="tp-input"><label>Nom</label>
                  <input value={p.nom} onChange={(e) => setPaxField(activePax, "nom", e.target.value)} data-testid="flight-pax-nom" placeholder="Nom" />
                </label>
                <label className="tp-input"><label>Prénom</label>
                  <input value={p.prenom} onChange={(e) => setPaxField(activePax, "prenom", e.target.value)} data-testid="flight-pax-prenom" placeholder="Prénom" />
                </label>
                <label className="tp-input"><label>Date de naissance</label>
                  <input type="date" value={p.dob} onChange={(e) => setPaxField(activePax, "dob", e.target.value)} data-testid="flight-pax-dob" />
                </label>
                <label className="tp-input"><label>Nationalité</label>
                  <select value={p.nat} onChange={(e) => setPaxField(activePax, "nat", e.target.value)} data-testid="flight-pax-nat">
                    <option>Ivoirienne</option><option>Française</option><option>Autre</option>
                  </select>
                </label>
                <div style={{ display: "flex", gap: 9 }}>
                  <label className="tp-input" style={{ flex: "0 0 38%" }}><label>Pièce d'identité</label>
                    <select value={p.pieceType} onChange={(e) => setPaxField(activePax, "pieceType", e.target.value)} data-testid="flight-pax-piece-type">
                      <option>CNI</option><option>Passeport</option>
                    </select>
                  </label>
                  <label className="tp-input" style={{ flex: 1 }}><label>Numéro de pièce</label>
                    <input value={p.pieceNum} onChange={(e) => setPaxField(activePax, "pieceNum", e.target.value)} data-testid="flight-pax-piece-num" placeholder="CI0123456789" />
                  </label>
                </div>
              </div>
            ))}
            <PrimaryBtn testid="flight-pax-continue" disabled={!paxValid} onClick={() => setStep("options")}>Continuer</PrimaryBtn>
          </>
        )}

        {step === "options" && flight && (
          <>
            <div className="tp-summary">
              <span className="t"><h3>{from} → {to}</h3><p>{dateLabel} · {flight.dep} - {flight.arr}</p></span>
              <span className="tp-logo" style={{ background: "#0E7A4F" }}>ACI</span>
            </div>
            <div className="tp-sechead"><div><h2>Bagages supplémentaires</h2></div></div>
            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <div className="tp-card" style={{ flex: 1, marginBottom: 0, textAlign: "center", padding: "9px 6px" }}>
                <Luggage size={16} color="#0E7A4F" /><div style={{ fontSize: 10.5, fontWeight: 800 }}>Bagage cabine</div><div style={{ fontSize: 9.5, color: "#7B8B7F" }}>Inclus</div>
              </div>
              <div className="tp-card" style={{ flex: 1, marginBottom: 0, textAlign: "center", padding: "9px 6px" }}>
                <Briefcase size={16} color="#0E7A4F" /><div style={{ fontSize: 10.5, fontWeight: 800 }}>Bagage soute</div><div style={{ fontSize: 9.5, color: "#7B8B7F" }}>Inclus</div>
              </div>
            </div>
            <OptRow testid="flight-opt-bag" title="Bagage supplémentaire" sub="23 kg en soute" price={`+ ${fmt(FLIGHT_OPTIONS.extraBag)}`}
              checked={opts.extraBag} onToggle={() => setOpts((o) => ({ ...o, extraBag: !o.extraBag }))} />
            <div className="tp-sechead"><div><h2>Services additionnels</h2></div></div>
            <OptRow testid="flight-opt-seat" title="Choix du siège" sub="Sélection sur le plan de cabine" price={`+ ${fmt(FLIGHT_OPTIONS.seat)}`}
              checked={opts.seatOpt} onToggle={() => setOpts((o) => ({ ...o, seatOpt: !o.seatOpt }))}
              chevron={<ChevronRight size={14} color="#A6B2A9" />} />
            <OptRow testid="flight-opt-insurance" title="Assurance voyage" sub="Couverture en cas d'accident" price={`+ ${fmt(FLIGHT_OPTIONS.insurance)}`}
              checked={opts.insurance} onToggle={() => setOpts((o) => ({ ...o, insurance: !o.insurance }))} />
            <div className="tp-card">
              <PriceRow total label="Total actuel" value={fmt(flight.price * pax + (opts.extraBag ? FLIGHT_OPTIONS.extraBag : 0) + (opts.seatOpt && seat ? FLIGHT_OPTIONS.seat : 0) + (opts.insurance ? FLIGHT_OPTIONS.insurance : 0))} />
            </div>
            <PrimaryBtn testid="flight-options-continue" onClick={afterOptions}>Continuer</PrimaryBtn>
          </>
        )}

        {step === "seat" && flight && (
          <>
            <div className="tp-summary">
              <span className="t"><h3>{from} → {to}</h3><p>{dateLabel} · {flight.dep} - {flight.arr}</p></span>
            </div>
            <SeatLegend />
            <PlaneSeatMap taken={PLANE_TAKEN} selected={seat} onSelect={setSeat} />
            <div className="tp-card" style={{ marginTop: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }} data-testid="flight-seat-summary">
              <span><span style={{ fontSize: 10, color: "#7B8B7F", fontWeight: 700 }}>Siège sélectionné</span><br />
                <b style={{ fontSize: 15 }}>{seat || "—"}</b></span>
              <b style={{ color: "#0E7A4F" }}>{seat ? `+ ${fmt(FLIGHT_OPTIONS.seat)}` : ""}</b>
            </div>
            <PrimaryBtn testid="flight-seat-confirm" disabled={!seat} onClick={() => setStep("payment")}>Confirmer le siège</PrimaryBtn>
          </>
        )}

        {step === "payment" && flight && (
          <>
            <div className="tp-summary">
              <span className="t"><h3>{from} → {to}</h3><p>{dateLabel} · {flight.dep} - {flight.arr}</p></span>
              <span className="tp-logo" style={{ background: "#0E7A4F" }}>ACI</span>
            </div>
            <div className="tp-card" data-testid="flight-pay-recap">
              <PriceRow label={`Tarif passager${pax > 1 ? ` × ${pax}` : ""}`} value={fmt(flight.price * pax)} />
              {opts.extraBag && <PriceRow label="Bagage supplémentaire" value={fmt(FLIGHT_OPTIONS.extraBag)} />}
              {seat && <PriceRow label={`Choix du siège (${seat})`} value={fmt(FLIGHT_OPTIONS.seat)} />}
              {opts.insurance && <PriceRow label="Assurance voyage" value={fmt(FLIGHT_OPTIONS.insurance)} />}
              <PriceRow total label="Total" value={fmt(total)} />
            </div>
            <div className="tp-sechead"><div><h2>Mode de paiement</h2></div></div>
            <PaymentMethods value={payMethod} onChange={setPayMethod} />
            <PrimaryBtn testid="flight-pay-btn" disabled={paying} onClick={pay}>
              {paying ? "Paiement en cours…" : `Payer ${fmt(total)}`}
            </PrimaryBtn>
            <SecureNote />
          </>
        )}

        {step === "confirmation" && flight && (
          <>
            <div className="tp-confirm" data-testid="flight-confirmation">
              <span className="circ"><Check size={36} color="#fff" strokeWidth={3} /></span>
              <h2>Réservation confirmée !</h2>
              <p>Votre billet a été émis avec succès.</p>
            </div>
            <div className="tp-ref" data-testid="flight-booking-ref">
              <span><span className="lab">Référence réservation</span>{bookingRef}</span>
              <button className="tp-link" onClick={() => { navigator.clipboard?.writeText(bookingRef); toast.success("Référence copiée ✓"); }} aria-label="Copier"><Copy size={14} /></button>
            </div>
            <PrimaryBtn testid="flight-see-ticket" onClick={() => setStep("ticket")}>Voir mes billets</PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="flight-email-ticket" onClick={() => toast.success("Billet envoyé par email ✓")}>
              <Mail size={14} /> Recevoir par email
            </PrimaryBtn>
            <div className="tp-sechead"><div><h2>Détails du vol</h2></div></div>
            <div className="tp-card">
              <div className="tp-airline" style={{ marginBottom: 7 }}><span className="tp-logo" style={{ background: "#0E7A4F" }}>ACI</span>{flight.airline} · {flight.num} · {dateLabel}</div>
              <div className="tp-times">
                <span><span className="t">{flight.dep}</span><br /><span className="a">{from}</span></span>
                <span className="mid">{flight.duration}</span>
                <span style={{ textAlign: "right" }}><span className="t">{flight.arr}</span><br /><span className="a">{to}</span></span>
              </div>
            </div>
          </>
        )}

        {step === "ticket" && flight && (
          <>
            <TicketCard
              brand="Air Côte d'Ivoire" brandColor="#0E7A4F" badge="Billet validé"
              reference={bookingRef}
              passenger={`${passengers[0].civ} ${passengers[0].prenom} ${passengers[0].nom}`}
              from={{ code: from, city: airport(from).city }} to={{ code: to, city: airport(to).city }}
              depTime={flight.dep} arrTime={flight.arr}
              cells={[{ lab: "Vol", v: flight.num }, { lab: "Siège", v: seat || "Libre" }, { lab: "Classe", v: "Éco" }]}
            />
            <div className="tp-tbtns">
              <PrimaryBtn ghost testid="flight-ticket-share" onClick={shareTicket}><Share2 size={13} /> Partager</PrimaryBtn>
              <PrimaryBtn testid="flight-ticket-download" onClick={() => toast.success("Billet enregistré dans votre espace AYA ✓")}><Download size={13} /> Télécharger</PrimaryBtn>
            </div>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="flight-all-tickets" onClick={() => navigate("/mes-billets")}>Tous mes billets</PrimaryBtn>
            <div style={{ height: 8 }} />
            <PrimaryBtn ghost testid="flight-ticket-home" onClick={() => navigate("/search")}>Retour à l'accueil</PrimaryBtn>
          </>
        )}
      </div>
    </div>
  );
}
